// src/components/Customer/BookingSchedule.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock } from "lucide-react";

import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";

function safeNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function safeString(v, fallback = "") {
  return typeof v === "string" ? v : fallback;
}

async function loadPlatformFee() {
  try {
    const snap = await getDoc(doc(db, "adminRules", "platformFees"));
    if (snap.exists()) {
      return safeNumber(snap.data()?.platformFee, 0);
    }
  } catch (e) {
    console.error(e);
  }
  return 0;
}

export default function BookingSchedule() {
  const navigate = useNavigate();
  const { jobId, requestId } = useParams();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [platformFee, setPlatformFee] = useState(0);

  // -------------------------
  // LOAD BOOKING (JOB OR REQUEST)
  // -------------------------
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError("");

        if (!jobId && !requestId)
          throw new Error("Missing jobId or requestId in route");

        // --------- JOB ----------
        if (jobId) {
          const snap = await getDoc(doc(db, "jobs", jobId));
          if (!snap.exists()) throw new Error("Job not found");

          const data = snap.data() || {};
          
          // 🔥 NEW: Prevent access if already paid
          if (data.paymentStatus === "paid") {
            navigate("/customer/home", { replace: true });
            return;
          }

          if (!cancelled) {
            setBooking({
              id: snap.id,
              source: "job",
              subcategoryId: safeString(data.subcategoryId),
              preferredDate: safeString(data.preferredDate),
              preferredTime: safeString(data.preferredTime),
              total: safeNumber(data.price),
              taskerId: safeString(data.taskerId),
              customerId: safeString(data.customerId),
            });
          }
        }

        // --------- SERVICE REQUEST ----------
        if (requestId) {
          const snap = await getDoc(doc(db, "serviceRequests", requestId));
          if (!snap.exists()) throw new Error("Request not found");

          const data = snap.data() || {};
          
          if (data.paymentStatus === "paid") {
            navigate("/customer/home", { replace: true });
            return;
          } 

          if (!cancelled) {
            setBooking({
              id: snap.id,
              source: "request",
              subcategoryId: safeString(data.subcategoryId),
              preferredDate: safeString(data.preferredDate),
              preferredTime: safeString(data.preferredTime),
              total: safeNumber(data.budget),
              taskerId: "", // no tasker yet
              customerId: safeString(data.customerId),
            });
          }
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => (cancelled = true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, requestId]);

  // -------------------------
  // LOAD PLATFORM FEE
  // -------------------------
  useEffect(() => {
    (async () => {
      const pf = await loadPlatformFee();
      setPlatformFee(pf);
    })();
  }, []);

  // -------------------------
  // PRICING
  // -------------------------
  const total = useMemo(
    () => safeNumber(booking?.total, 0),
    [booking]
  );

  const serviceFee = useMemo(
    () => Math.max(total - safeNumber(platformFee, 0), 0),
    [total, platformFee]
  );

  // -------------------------
  // PROCEED
  // -------------------------
  const handleProceed = () => {
    if (!booking) return;

    navigate(`/customer/payment/${booking.source}/${booking.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading booking...
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        {error || "Booking not found"}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-[#1E3A8A]">Confirm Booking</h2>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg p-6 shadow-sm space-y-6">
          <div className="space-y-4">
            <h3 className="text-[#1E3A8A]">Booking Details</h3>

            <div className="flex items-start gap-3">
              <Calendar size={20} className="text-[#006C35]" />
              <div>
                <div className="text-gray-600">Date</div>
                <div className="text-[#1E3A8A]">
                  {booking.preferredDate || "—"}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock size={20} className="text-[#006C35]" />
              <div>
                <div className="text-gray-600">Time</div>
                <div className="text-[#1E3A8A]">
                  {booking.preferredTime || "—"}
                </div>
              </div>
            </div>

            <div>
              <div className="text-gray-600">Service</div>
              <div className="text-[#1E3A8A]">
                {booking.subcategoryId} Service
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6 space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Service Fee</span>
              <span className="text-[#1E3A8A]">{serviceFee} SAR</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Platform Fee</span>
              <span className="text-[#1E3A8A]">{platformFee} SAR</span>
            </div>
            <div className="border-t border-gray-200 pt-3 flex justify-between">
              <span>Total</span>
              <span className="text-[#006C35]">{total} SAR</span>
            </div>
          </div>

          <button
            onClick={handleProceed}
            className="w-full bg-[#006C35] text-white py-4 rounded-lg hover:bg-[#005428]"
          >
            Proceed to Payment
          </button>
        </div>
      </div>
    </div>
  );
}